import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import { debounce } from 'lodash';
import CoreSplitter from '@components-clean/core/core-splitter';
import { FullPageInfoField, InfoField } from '@components-clean/core/info-field';
import CmControlChartsGrid from '@pages-clean/risk-assessment/cm-controls/cm-control-charts.grid';
import {
    CmControlsContext,
    IFormData,
    SelectionState,
} from '@pages-clean/risk-assessment/cm-controls/interfaces/context';
import { CmControlsRisksGrid } from '@pages-clean/risk-assessment/cm-controls/risks.grid';
import { NullableStudy } from '@contexts/local-state-context';
import { SplitterOrientation } from '@utils/constants';
import { getIsActiveField } from '@utils/convertor';
import { LoggedInPageProps } from '@config/routes/interfaces';
import './index.scss';

interface IManageCmControlsProps {
    study: NullableStudy | undefined;
}

const Content = ({ study }: IManageCmControlsProps) => {
    const [selectionState, setSelectionState] = useState<SelectionState>({});
    const [formData, setFormData] = useState<IFormData>({
        study,
        selectionState,
    });

    const StudyHasCM2 = study && getIsActiveField('CM2', study.modules);

    const manageCmControlsProviderValue = useMemo(
        () => ({
            current: formData,
            setCurrent: setFormData,
            setSelectionState,
        }),
        [formData, setFormData, selectionState],
    );

    const { t } = useTranslation('ram', { keyPrefix: 'manageCmControls' });
    const { t: tHome } = useTranslation('home');

    useEffect(() => {
        setFormData({
            ...formData,
            study,
            selectionState,
        });
    }, [study?.id, selectionState]);

    const [panes, setPanes] = useState<SplitterPaneProps[]>([{ size: '50%', min: '15px' }, { min: '35px' }]);
    const onPaneChange = (event: SplitterOnChangeEvent) => {
        debounce(() => setPanes(event.newState), 20);
    };

    if (!StudyHasCM2)
        return <FullPageInfoField message={tHome('home:pageDataErrors:cannotAccessModule')} type="Info" />;

    return (
        <div className="cmControls-container">
            <CmControlsContext.Provider value={manageCmControlsProviderValue}>
                <div style={{ height: 'calc(100% - 30px)' }}>
                    <CoreSplitter
                        className="!k-h-full"
                        orientation={SplitterOrientation.vertical}
                        data-testid="risk-monitoring-manage-cm-controls-splitter"
                        alignmentValues={panes}
                        onChange={onPaneChange}
                    >
                        <div className="k-h-full">
                            <CmControlChartsGrid />
                        </div>
                        <div className="k-h-full">
                            {selectionState.selectedItem === undefined ? (
                                <div className="k-h-full">
                                    <InfoField type="Info" message={t('noParentRecordSelected')} layout="center" />
                                </div>
                            ) : (
                                <CmControlsRisksGrid studyRef={study?.id} />
                            )}
                        </div>
                    </CoreSplitter>
                </div>
            </CmControlsContext.Provider>
        </div>
    );
};

const ManageCmControlsWrapper = ({ study }: IManageCmControlsProps) => {
    const content = <Content study={study} />;

    return <>{content}</>;
};

const ManageCmControls = ({ study }: LoggedInPageProps) => <ManageCmControlsWrapper study={study} />;

export default ManageCmControls;
